import React from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import Graph from './components/Graph';
import EmigratePage from './components/EmigratePage';
// import EmailCapture from './components/EmailCapture';
import CountryDetailPage from './pages/CountryDetailPage';
import BlogIndexPage from './pages/BlogIndexPage';
import BlogPostPage from './pages/BlogPostPage';
import SaveMoneyPage from './pages/SaveMoneyPage';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import { useLanguage } from './context/LanguageContext';
import { useT } from './translations';

const TAB_ROUTES = ['/', '/emigreren', '/blog', '/besparen'];

const App = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { language, toggleLanguage } = useLanguage();
	const t = useT(language);

	const activeTab = TAB_ROUTES.findIndex((r) =>
		r === '/' ? location.pathname === '/' : location.pathname.startsWith(r)
	);
	const tabValue = activeTab === -1 ? 0 : activeTab;

	const handleTabChange = (_, val) => navigate(TAB_ROUTES[val]);

	return (
		<Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
			{/* Animated gradient header */}
			<Box
				sx={{
					background: 'linear-gradient(135deg, #312e81 0%, #4f46e5 40%, #6366f1 75%, #3730a3 100%)',
					backgroundSize: '250% 250%',
					animation: 'headerGradient 14s ease infinite',
					color: 'white',
					py: 5,
					position: 'relative',
					overflow: 'hidden',
				}}
			>
				{/* Decorative upward-growth chart line */}
				<Box
					sx={{
						position: 'absolute',
						inset: 0,
						pointerEvents: 'none',
						opacity: 0.13,
					}}
				>
					<svg
						viewBox="0 0 400 120"
						preserveAspectRatio="xMaxYMax meet"
						style={{ position: 'absolute', bottom: 0, right: 0, width: '45%', height: '100%' }}
					>
						<path
							className="chart-line"
							d="M 0,115 C 60,95 100,75 150,58 S 240,28 295,14 L 400,3"
							fill="none"
							stroke="white"
							strokeWidth="2.5"
							strokeLinecap="round"
						/>
						<circle cx="400" cy="3" r="4" fill="white" opacity="0.8" />
					</svg>
				</Box>

				<Container maxWidth="md" sx={{ position: 'relative' }}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
						<Box>
							<Typography variant="h3" fontWeight="bold" gutterBottom className="animate-fade-up">
								{t('siteTitle')}
							</Typography>
							<Typography
								variant="h6"
								className="animate-fade-up"
								style={{ '--delay': '0.15s' }}
								sx={{ opacity: 0.85, maxWidth: 600 }}
							>
								{t('siteSubtitle')}
							</Typography>
						</Box>
						<Button
							onClick={toggleLanguage}
							size="small"
							sx={{
								color: 'white',
								border: '1px solid rgba(255,255,255,0.5)',
								borderRadius: 2,
								px: 1.5,
								py: 0.5,
								minWidth: 'auto',
								fontWeight: 700,
								fontSize: 13,
								letterSpacing: 0.5,
								flexShrink: 0,
								mt: 0.5,
								'&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderColor: 'white' },
							}}
						>
							{language === 'nl' ? 'EN' : 'NL'}
						</Button>
					</Box>
				</Container>
			</Box>

			<Box
				className="animate-fade-in"
				style={{ '--delay': '0.2s' }}
				sx={{ bgcolor: 'white', borderBottom: 1, borderColor: 'divider' }}
			>
				<Container maxWidth="md">
					<Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
						{[t('tabPlanner'), t('tabEmigrate'), t('tabBlog'), t('tabSave')].map((label, i) => (
							<Box
								key={i}
								onClick={() => handleTabChange(null, i)}
								sx={{
									flex: { xs: '1 1 50%', md: '1 1 auto' },
									textAlign: 'center',
									py: 1.5,
									px: 1,
									cursor: 'pointer',
									fontSize: 14,
									fontWeight: tabValue === i ? 700 : 500,
									color: tabValue === i ? 'primary.main' : 'text.secondary',
									borderBottom: '2px solid',
									borderColor: tabValue === i ? 'primary.main' : 'transparent',
									'&:hover': { bgcolor: 'rgba(0,0,0,0.04)', color: 'primary.main' },
									transition: 'color 0.15s',
									boxSizing: 'border-box',
									userSelect: 'none',
								}}
							>
								{label}
							</Box>
						))}
					</Box>
				</Container>
			</Box>

			<Container maxWidth="md" sx={{ py: 5 }}>
				<Routes>
					<Route
						path="/"
						element={
							<div className="tab-content">
								<Graph onEmigrate={() => navigate('/emigreren')} />

								{/* <EmailCapture /> */}

								<Divider sx={{ my: 4 }} />

								<Typography variant="body1" color="text.secondary" paragraph>
									{t('homeIntro')}{' '}
									<a
										href="https://www.youtube.com/watch?v=3tcHzIcrdms"
										target="_blank"
										rel="noopener noreferrer"
									>
										{t('homeDreamLink')}
									</a>
									{t('homeBody1')}
								</Typography>

								<Typography variant="body1" color="text.secondary" paragraph>
									{t('homeBody2')} <strong>{t('homeBody2KDM')}</strong>{t('homeBody2Colon')}{' '}
									<strong>{t('homeBody2Desc')}</strong>
									{t('homeBody2End')} <strong>{t('homeBody2Monthly')}</strong> {t('homeBody2Final')}
								</Typography>

								<Typography variant="caption" color="text.disabled">
									{t('homeSponsor')}
								</Typography>
							</div>
						}
					/>
					<Route
						path="/emigreren"
						element={
							<div className="tab-content">
								<EmigratePage />
							</div>
						}
					/>
					<Route path="/emigreren/:land" element={<CountryDetailPage />} />
					<Route
						path="/blog"
						element={
							<div className="tab-content">
								<BlogIndexPage />
							</div>
						}
					/>
					<Route path="/blog/:slug" element={<BlogPostPage />} />
					<Route
						path="/besparen"
						element={
							<div className="tab-content">
								<SaveMoneyPage language={language} />
							</div>
						}
					/>
					<Route
						path="*"
						element={
							<Box sx={{ textAlign: 'center', py: 8 }}>
								<Typography variant="h4" gutterBottom>
									{t('notFound')}
								</Typography>
								<Typography
									variant="body1"
									color="text.secondary"
									sx={{ cursor: 'pointer' }}
									onClick={() => navigate('/')}
								>
									{t('backToPlanner')}
								</Typography>
							</Box>
						}
					/>
				</Routes>
			</Container>
		</Box>
	);
};

export default App;
